import React, { useCallback, useMemo, useState } from 'react';

import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import { StyledAlertIcon } from '@zola/zola-ui/src/components/SectionMessage/SectionMessage.styles';
import { EditIcon } from '@zola/zola-ui/src/components/SvgIcons';

import Styled from './SummarySection.styles';

type SummarySectionProps = {
  /** Header text for the section */
  hed: React.ReactNode;
  /** Content for the section when the summary loads.  Required.  Can be form elements */
  view: React.ReactNode;
  /** Optional Content for the section when edit is clicked.   */
  edit?: React.ReactNode;

  misMatch?: React.ReactNode;

  /**
   * Continue to show the hed during edit.  True by default.
   */
  showHedOnEdit?: boolean;

  required?: boolean;

  isMisMatch?: boolean;

  /**
   * Callback invoked when the content is expanded
   */
  onExpanded?: () => void;
};

/**
 * Header and edit controls for a section of the rapid repeat / mass inquiry screen.
 *
 */
export const SummarySection = ({
  view,
  edit,
  misMatch,
  hed,
  showHedOnEdit = true,
  isMisMatch = false,
  onExpanded,
  required,
}: SummarySectionProps): JSX.Element => {
  const [expanded, setExpanded] = useState(edit === undefined);

  const showEditContent = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      setExpanded(true);
      if (onExpanded) {
        onExpanded();
      }
    },
    [onExpanded]
  );

  const expandedHed = useMemo(() => {
    if (isMisMatch) {
      return (
        <Styled.AlertNotice>
          <StyledAlertIcon />
          {hed}
          {required && <Styled.RequiredNotice>*</Styled.RequiredNotice>}
        </Styled.AlertNotice>
      );
    }
    return hed;
  }, [hed, isMisMatch, required]);

  const renderContent = useCallback(() => {
    if (isMisMatch && misMatch) {
      return misMatch;
    }
    if (expanded) {
      return edit || view;
    }
    return view;
  }, [edit, expanded, isMisMatch, misMatch, view]);

  const renderHed = useCallback(() => {
    if (isMisMatch && misMatch) {
      return <Styled.Header>{expandedHed}</Styled.Header>;
    }

    if (edit) {
      if (!expanded) {
        return (
          <Styled.Header canEdit>
            {expandedHed}
            <LinkV2 role="button" onClick={showEditContent} type="button">
              <EditIcon height={14} width={14} title="Edit" />
            </LinkV2>
          </Styled.Header>
        );
      }
      if (!showHedOnEdit) return null;
    }
    return (
      <Styled.Header>
        {expandedHed}
        {required && <Styled.RequiredNotice>*</Styled.RequiredNotice>}
      </Styled.Header>
    );
  }, [edit, expanded, expandedHed, isMisMatch, misMatch, required, showEditContent, showHedOnEdit]);

  return (
    <Styled.SummarySection>
      {renderHed()}
      {renderContent()}
    </Styled.SummarySection>
  );
};
